import Alert from '@amzn/meridian/alert';
import { AlertType } from '@amzn/meridian/alert/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { FC, ReactNode } from 'react';
import { ItemImage } from '../../blocks';

type HelpImageSize = 'icon' | 'small' | 'medium' | 'large' | 'xlarge';

const HELP_IMAGE_MAX_WIDTHS: Record<HelpImageSize, string> = {
    icon: '30px',
    small: '300px',
    medium: '600px',
    large: '900px',
    xlarge: '1200px',
};

export const HelpImage: FC<{ src: string; size?: HelpImageSize }> = ({ src, size = 'large' }) => (
    <ItemImage src={src} maxWidth={HELP_IMAGE_MAX_WIDTHS[size]} />
);

export const HelpTitle: FC<{ children: ReactNode }> = ({ children }) => (
    <Row width={'100%'} alignmentHorizontal={'center'}>
        <Text type={'h200'} data-help-type={'title'}>
            {children}
        </Text>
    </Row>
);

export const HelpBody: FC<{ children: ReactNode }> = ({ children }) => (
    <Text type={'b300'} data-help-type={'body'} alignment={'justify'}>
        {children}
    </Text>
);

export const HelpSubheading: FC<{ children: ReactNode }> = ({ children }) => (
    <Text type={'h100'} data-help-type={'subheading'} alignment={'justify'}>
        {children}
    </Text>
);

export const HelpStep: FC<{ step: number; group?: string; children: ReactNode }> = ({ step, group, children }) => {
    return (
        <Row spacing={'small'} alignmentVertical={'top'} alignmentHorizontal={'left'}>
            <Text type={'h200'}>{step}</Text>
            <Text type={'b300'} data-help-type={'step'} {...(group && { 'data-help-step-group': group })}>
                {children}
            </Text>
        </Row>
    );
};

const DEFAULT_MARKERS: Record<number, string> = {
    0: '•',
    1: '◦',
};

export const HelpBullet: FC<{ children: ReactNode; level?: 0 | 1; marker?: string }> = ({
    children,
    level = 0,
    marker,
}) => {
    const defaultMarker = DEFAULT_MARKERS[level];
    return (
        <Row spacing={'small'} alignmentVertical={'top'} alignmentHorizontal={'left'}>
            <Text type={'b300'}>{marker || defaultMarker}</Text>
            <Text type={'b300'} data-help-type={`bullet-${level}`} {...(marker && { 'data-help-marker': marker })}>
                {children}
            </Text>
        </Row>
    );
};

export const HelpIndent: FC<{ children: ReactNode }> = ({ children }) => (
    <Column spacingInset={'none none none 400'} spacing={'xsmall'}>
        {children}
    </Column>
);

export const HelpCaptionedImage: FC<{ children: ReactNode; image: string }> = ({ children, image }) => (
    <div data-help-type={'captioned-image'}>
        <Row widths={['30%', '70%']}>
            <Column>{children}</Column>
            <ItemImage src={image} />
        </Row>
    </div>
);

export const HelpAlert: FC<{ children: ReactNode; type?: AlertType }> = ({ children, type = 'informational' }) => (
    <Alert type={type} data-help-type={'alert'}>
        {children}
    </Alert>
);
